import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { NgRedux } from '@angular-redux/store';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {
    ApplicationFacade,
    CustomerFacade,
    NotificationFacade,
    PersonalizeFacade,
    RelatedCardFacade
} from '@ridango/components';
import { BaseComponent, CoreFacade, AuthService, PersonalCodeValidator, RelatedCard } from '@ridango/core';

import { Observable } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import Env from '@ridango/core/env';

@Component({
    selector: 'rwidget-related-card-personalize',
    templateUrl: './related-card-personalize.component.html',
    styleUrls: ['./related-card-personalize.component.scss']
})
export class RelatedCardPersonalizeComponent extends BaseComponent implements OnInit, OnDestroy {
    highContrast$: Observable<boolean> = this.applicationFacade.highContrast$;
    personalizingLoading$: Observable<boolean> = this.personalizeFacade.isPersonalizingLoading$;
    travelCards$: Observable<RelatedCard[]> = this.relatedCardFacade.typeTravelcardCards$.pipe(
        map((relatedCards) => relatedCards.filter((card) => ['U', 'T', 'W'].includes(card.card_type)))
    );

    _personalizeForm: FormGroup;
    terms = null;
    termsUrl: string;
    termsAgreed = false;
    termsVisible = false;
    customerPersonalCode: string;
    isUserPersonalized = this.ridangoAuth.getLoginStatus() && this.ridangoAuth.getAccountPersonalizationState();
    isStronglyAuth = !!this.env?.PERSONALIZATION_WITHOUT_ID_DOC && this.isUserPersonalized;
    beneficial = false;

    travelCards = [];

    @ViewChild('modal') termsModalEl;
    @ViewChild('modalTrigger') modalTriggerEl;

    constructor(
        @Inject('ENV') public env: Env,
        private personalCodeValidator: PersonalCodeValidator,
        private coreFacade: CoreFacade,
        private ridangoAuth: AuthService,
        private relatedCardFacade: RelatedCardFacade,
        public translate: TranslateService,
        private customerFacade: CustomerFacade,
        private ngRedux: NgRedux<any>,
        private router: Router,
        private applicationFacade: ApplicationFacade,
        private notification: NotificationFacade,
        private personalizeFacade: PersonalizeFacade
    ) {
        super();
    }

    ngOnInit() {
        this.terms = this.getTerms();
        this.relatedCardFacade.fetchAllRelatedCards();

        this.travelCards$.pipe(takeUntil(this.componentDestroyed$)).subscribe((relatedCards) => {
            this.travelCards = relatedCards;
        });

        this.customerFacade.getCustomerProfile();

        this.highContrast$.pipe(takeUntil(this.componentDestroyed$)).subscribe((highContrast) => {
            this.termsUrl = this.getTermsUrl(highContrast, this.terms.content_base_url, this.translate.currentLang);
        });

        this._personalizeForm = new FormGroup({
            card_id: new FormControl('', [Validators.required, Validators.pattern('[0-9]*')]),
            terms: new FormControl(this.termsAgreed, Validators.required),
            personal_code: new FormControl('', [Validators.required, this.personalCodeValidator.getValidator()])
        });

        this.customerFacade.profile$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(({ p_code, authenticated, personal_code }) => {
                if (authenticated === undefined) {
                    return;
                }

                if (!this.ridangoAuth.getLoginStatus()) {
                    this.isUserPersonalized = false;
                    this.isStronglyAuth = false;
                    this.syncIdCardControl();
                    return;
                }

                this.ridangoAuth.setAccountPersonalizationState(authenticated);
                this.isUserPersonalized = authenticated;
                this.isStronglyAuth = !!this.env?.PERSONALIZATION_WITHOUT_ID_DOC && this.isUserPersonalized;
                this.syncIdCardControl();

                if (p_code && !this.isStronglyAuth) {
                    this.customerPersonalCode = p_code;
                } else if (personal_code && this.isUserPersonalized) {
                    this.customerPersonalCode = personal_code;
                }
            });

        this.personalizeFacade.isPersonalizingFinished$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((isPersonalizingFinished) => {
                if (isPersonalizingFinished) {
                    this.personalizeFacade.setPersonalizedLoading(false);
                    this.personalizeFacade.error$.pipe(take(1)).subscribe((error) => {
                        if (error) {
                            this.handleError(this.translate.instant(error));
                        } else {
                            this.notification.success(this.translate.instant('LOCAL_PERSONALIZATION_SUCCESS'));
                        }
                    });
                    this.scrollToTop();
                }
            });

        this.syncIdCardControl();
    }

    syncIdCardControl() {
        const hasIdCardControl = !!this._personalizeForm.controls.id_card;

        if (!this.isStronglyAuth && !hasIdCardControl) {
            this._personalizeForm.addControl(
                'id_card',
                new FormControl('', [Validators.required, Validators.pattern('[A-Z]{1,2}[0-9]{7}')])
            );
        } else if (this.isStronglyAuth && hasIdCardControl) {
            this._personalizeForm.removeControl('id_card');
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.personalizeFacade.resetPersonalizedState();
    }

    /* Methods */
    handleError(e: any) {
        if (e.error.description) {
            this.notification.error(this.translate.instant(e.error.description));
        } else {
            this.notification.error(this.translate.instant(e.error));
        }
    }

    // TODO: It should come from CoreFacade
    getTerms() {
        return this.ngRedux.getState().core.region.terms;
    }

    getTermsUrl(highContrast = false, baseUrl: string, locale: string) {
        let termsUrl = `${baseUrl}/${locale}/`;
        if (highContrast) {
            termsUrl += '#high-contrast';
        }
        return termsUrl;
    }

    toggleCheckbox() {
        this.termsAgreed = !this.termsAgreed;
        this._personalizeForm.controls['terms'].patchValue(this.termsAgreed);
    }

    toggleBeneficialCheckbox() {
        this.beneficial = !this.beneficial;
    }

    openTerms() {
        this.termsVisible = true;
        setTimeout(() => this.termsModalEl.firstFocus.nativeElement.focus());
    }

    closeTerms() {
        this.termsVisible = false;
        setTimeout(() => this.modalTriggerEl.nativeElement.focus());
    }

    agreeTerms(agreement) {
        this.termsAgreed = agreement;
        this._personalizeForm.controls['terms'].patchValue(this.termsAgreed);
        this.closeTerms();
    }

    submitForm() {
        if (this._personalizeForm.valid) {
            const payload = this._personalizeForm.getRawValue();
            delete payload.terms;

            this.personalizeFacade.setPersonalizedLoading(true);

            payload.api_version = 2;
            payload.region_id = this.env.REGION_ID;
            payload.isStrongAuth = this.isStronglyAuth;
            payload.terms_version = this.terms.id;
            if (this.env.IS_NEW_AFC) {
                payload.beneficial = this.beneficial;
            } else {
                payload.beneficial = this.beneficial ? 'Y' : 'N';
            }

            this.personalizeFacade.savePersonalizedCard(payload);
        }
    }
    scrollToTop() {
        document.querySelector('rwidget-alert')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}
